<script setup>
import { OrderStatus } from "@classes-service-types/order-status";
import { computed, ref, toRefs } from "vue";
import { useRouter } from "vue-router";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import OrderDetails from "@/components/OrderDetails/OrderDetails.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { useCustomPriceFees } from "@/composables/authorization.js";
import {
  APPLICATION_FEE_DATA,
  BILLING_PARTY_INSTITUTION,
  CONFIRMATION_FEE_IN_CENTS,
  OLD_APPLICATION_FEE_AMOUNT,
} from "@/constants";
import { dollarsToCents, formattedPriceFromCents as formattedPrice } from "@/util/formatter";
import { REVIEW_PAYMENT_SCHEDULE_FEE_LABELS, RegistrationStage } from "../constants";
import { buildOrderQueryParams } from "../utils/utils";

const props = defineProps({
  // see useApplication.js for application definition
  application: {
    type: Object,
    default: () => {},
    required: true,
  },
  goToNextStep: {
    type: Function,
    default: () => {},
    required: true,
  },
});

const { application } = toRefs(props);
const router = useRouter();
const submissionError = ref("");
const reviewSubmit = ref(null);
const pricing = ref(null);

// Computed
const {
  applicationFeePayment,
  applicationFeePaymentIsLoading,
  appliedAgreement,
  profile,
  primaryOrder,
  session,
  sessionId,
  sessionApplicationFee,
  sessionConfirmationDeposit,
  sessionFeesLoading,
  programPage,
  programPageIsLoading,
} = application.value;

const homeInstitutionId = computed(() => profile.value?.colleges?.[0]?.college_id ?? "");
const isPersonalized = computed(() => session.value?.is_personalized);
// Hide the price breakdown while the program page loads, and once loaded when it is configured not to
// display a starting price. Holding during load avoids briefly flashing prices that then disappear.
const hidePrices = computed(() => programPageIsLoading.value || programPage.value?.display_starting_price === false);
const isOrderEditable = computed(() =>
  [OrderStatus.Draft, OrderStatus.Submitted].includes(primaryOrder.value?.status),
);
const opeId = computed(() => primaryOrder.value?.ope_id);
const displayApplicationFee = computed(() => !(appliedAgreement.value?.applicationFee === BILLING_PARTY_INSTITUTION));
const displayConfirmationPayment = computed(
  () =>
    !(appliedAgreement.value?.confirmationPayment === BILLING_PARTY_INSTITUTION) &&
    // Until the amount resolves the row stays, showing its skeleton, so a deposit that is merely
    // slow to load is not mistaken for a configured $0 and hidden.
    (sessionFeesLoading.value || confirmationPaymentInCents.value !== 0),
);
const displayPaymentSchedule = computed(() => !appliedAgreement.value?.direct_billing_type);
const paymentScheduleIsExpanded = ref(false);
const applicationFee = computed(() => {
  if (applicationFeePayment.value && applicationFeePayment.value > -1) {
    return {
      amountInCents: dollarsToCents(applicationFeePayment.value),
      label: REVIEW_PAYMENT_SCHEDULE_FEE_LABELS.totalPaid,
    };
  }
  const cents = sessionApplicationFee.cents.value;
  if (cents !== null && cents !== undefined) {
    return {
      amountInCents: cents,
      label: REVIEW_PAYMENT_SCHEDULE_FEE_LABELS.dueToday,
    };
  }
  return {
    amountInCents: dollarsToCents(APPLICATION_FEE_DATA.amount),
    label: REVIEW_PAYMENT_SCHEDULE_FEE_LABELS.dueToday,
  };
});

const confirmationPaymentInCents = computed(() => {
  if (sessionConfirmationDeposit.cents.value !== null && sessionConfirmationDeposit.cents.value !== undefined) {
    return sessionConfirmationDeposit.cents.value;
  }
  return !useCustomPriceFees.value ? CONFIRMATION_FEE_IN_CENTS : 0;
});

const showApplicationFeeSkeleton = computed(() => applicationFeePaymentIsLoading.value || sessionFeesLoading.value);

const showConfirmationDepositSkeleton = computed(() => sessionFeesLoading.value);

const finalPayment = computed(() => {
  let total = pricing.value?.student_total_price_in_cents;
  if (!total) {
    return 0;
  }
  total = total - confirmationPaymentInCents.value;
  if (!APPLICATION_FEE_DATA.flagActive || applicationFeePayment.value === dollarsToCents(OLD_APPLICATION_FEE_AMOUNT)) {
    total = total - applicationFee.value.amountInCents;
  }
  return total;
});

const initialPersonalizeLocation = computed(() => {
  if (!primaryOrder.value?.id) {
    return "";
  }

  return {
    name: "modify-application",
    params: {
      applicationId: application.value.applicationId.value,
      stage: RegistrationStage.PERSONALIZE,
    },
    query: buildOrderQueryParams(primaryOrder),
  };
});

// Methods
const submitReview = async () => {
  try {
    reviewSubmit.value.startLoading();
    props.goToNextStep();
  } catch (e) {
    submissionError.value = e;
  } finally {
    reviewSubmit.value.stopLoading();
  }
};
</script>

<template>
  <div class="text-indigo-base text-left bg-white w-full max-w-full min-w-0 px-4 pb-4 md:px-0">
    <div class="grid grid-cols-1 gap-6 md:gap-7 md:pt-4">
      <div>
        <h1 class="font-bold text-[28px] uppercase" data-testid="review-headline">
          Take a moment to make sure all of your selections look just right!
        </h1>
        <p v-if="isPersonalized && isOrderEditable" class="font-medium text-xl pt-10" data-testid="review-copy">
          <router-link
            :class="`${secondaryColorClassLinks} font-semibold hover:underline`"
            :to="initialPersonalizeLocation"
            >Go back</router-link
          >
          if something doesn’t look right. <br />
          <br />
          Don’t worry, we’ll save your progress so you can pick up right where you left off!
        </p>
      </div>

      <div
        class="flex flex-col w-full py-4 px-6 gap-8 md:m-auto"
        :class="`${secondaryColor}` ? 'bg-gray-275' : 'bg-teal-light'"
      >
        <order-details
          :current-order="primaryOrder"
          :is-personalized="isPersonalized"
          :program-session="session"
          :ope8-id="opeId"
          :home-institution-id="homeInstitutionId"
          :hide-prices="hidePrices"
          @get-pricing="(_pricing) => (pricing = _pricing)"
        />
        <template v-if="displayPaymentSchedule && !hidePrices">
          <hr class="mt-1 border-configurator-order-summary-gray" />
          <div>
            <div
              class="grid grid-cols-2 cursor-pointer"
              data-testid="price-breakdown-toggler"
              @click="() => (paymentScheduleIsExpanded = !paymentScheduleIsExpanded)"
            >
              <div v-if="pricing" class="flex items-center justify-between col-span-2">
                <p class="text-base block w-full text-indigo-base">
                  <span>Payment Schedule</span>
                  <template v-if="displayApplicationFee">
                    <SkeletonLoader
                      v-if="showApplicationFeeSkeleton"
                      rounded
                      class="ml-8 w-1/3 float-right"
                    />
                    <span v-else class="ml-8 font-bold"
                      >{{ applicationFee.label }}: {{ formattedPrice(applicationFee.amountInCents) }}</span
                    >
                  </template>
                </p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-6 h-6"
                  :class="paymentScheduleIsExpanded ? 'transform rotate-180' : ''"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <hr v-if="paymentScheduleIsExpanded" class="col-span-2 my-4 border-configurator-order-summary-gray" />
              <div v-if="paymentScheduleIsExpanded" class="col-span-2 space-y-2 mt-2 text-base">
                <div v-if="displayConfirmationPayment" class="flex justify-between items-center">
                  <div class="text-indigo-base" data-testid="housing-fee-label">
                    Confirmation Payment
                  </div>
                  <SkeletonLoader
                    v-if="showConfirmationDepositSkeleton"
                    rounded
                    class="ml-8 w-1/3 float-right"
                  />
                  <div v-else class="font-bold text-indigo-base">
                    {{ formattedPrice(confirmationPaymentInCents) }}
                  </div>
                </div>
                <p class="text-xs">
                  Payment deadline - Two weeks after acceptance
                </p>
                <div class="flex justify-between items-center">
                  <div class="text-indigo-base">Final Payment</div>
                  <SkeletonLoader
                    v-if="showConfirmationDepositSkeleton"
                    rounded
                    class="ml-8 w-1/3 float-right"
                  />
                  <div v-else class="font-bold text-indigo-base" data-testid="housing-fee-value">
                    {{ formattedPrice(finalPayment) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <ButtonWithSpinner
          id="reviewButton"
          ref="reviewSubmit"
          data-cy="reviewButton"
          type="submit"
          variant="secondary"
          variant-type="block"
          button-height="min-h-[58px] md:min-h-[60px]"
          :grey-disabled-class="true"
          @click="submitReview"
        >
          <div class="flex items-center justify-center">
            <span class="pr-3 uppercase" data-testid="review-summary-text">
              <!-- Todo: Think, is text different if group visa is the next step, instead of checkout? -->
              Next
            </span>
            <ArrowRight custom-class="w-4 h-4" />
          </div>
        </ButtonWithSpinner>
      </div>

      <div v-if="submissionError" class="error text-error-900 mt-2">
        {{ submissionError }}
      </div>
    </div>
  </div>
</template>
